import router from '@/core/router.js';
import request from '@/utilities/request';
import { SIGNINTYPE, hashData } from '@/utilities/index';
import { clearPollingSession } from '@/utilities/pollingSession';
import { writeTmCaid } from '@/utilities/tmCaidCookie';

export default {
  async startReauth({ state }, { email }) {
    let recaptchaToken = '';
    let siteKey = '';

    try {
      const recaptchaResult = await state.recaptcha.execute('startReauth');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    return request.post('/signin/module.php/tmauth/startreauth', {
      recaptchaToken,
      siteKey,
      email,
    });
  },
  async checkAccount({ commit, state }, requestBody) {
    let recaptchaToken = '';
    let siteKey = '';
    let { email, phone } = requestBody;

    // Reset data
    commit('setCheckAccountEmail', '');
    commit('setCheckAccountPhone', '');
    commit('setCheckAccountExists', false);
    commit('setCheckAccountSupportAuthMethod', []);

    try {
      const recaptchaResult = await state.recaptcha.execute('checkAccount');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    return request.post('/signin/module.php/tmauth/checkaccount', {
      recaptchaToken,
      siteKey,
      ...requestBody,
    })
    .then((res) => {
      // Save check account response
      commit('setCheckAccountEmail', email || '');
      commit('setCheckAccountPhone', phone || '');
      commit('setCheckAccountExists', res.data.result === 'existed');
      commit('setCheckAccountSupportAuthMethod', res.data.support_auth_method || []);
      return res;
    });
  },
  async updateEmail({ state }, { new_email }) {
    let recaptchaToken = '';
    let siteKey = '';
    try {
      const recaptchaResult = await state.recaptcha.execute('updateEmail');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }
    return request.post('/signin/module.php/tmauth/updateemail', {
      new_email,
      recaptchaToken,
      siteKey,
    });
  },
  async updateEmailAndCompleteAuth({ state, commit }, { new_email, otp_code }) {
    commit('setIsVerifying', true);
    commit('setEmailVerificationCodeError', '');

    let recaptchaToken = '';
    let siteKey = '';
    try {
      const recaptchaResult = await state.recaptcha.execute('updateEmailAndCompleteAuth');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    try {
      const payload = {
        new_email,
        otp_code,
        recaptchaToken,
        siteKey,
      };
      console.log('[updateEmailAndCompleteAuth] Sending request with payload:', payload);
      const res = await request.post('/signin/module.php/tmauth/updateemailandcompleteauth', payload);
      commit('setIsVerifying', false);
      return res;
    } catch (error) {
      commit('setIsVerifying', false);
      if (error?.response?.status === 403) {
        commit('setEmailVerificationCodeError', state.lang.incorrectCode);
      } else {
        const errorMsg = error?.response?.data || 'Failed to update email and complete authentication';
        commit('setEmailVerificationCodeError', errorMsg);
      }
      throw error;
    }
  },
  async signInAuth({ commit, dispatch, state, getters }, { userdata, signInForm }) {
    commit('ableToSignIn', false);
    commit('setEmailError', '');
    commit('setPhoneError', '');

    let recaptchaToken = '';
    let siteKey = '';

    try {
      const recaptchaResult = await state.recaptcha.execute('signIn');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    request
      .post('/signin/module.php/tmauth/usersigninauth', {
        recaptchaToken,
        siteKey,
        ...userdata,
      })
      .then((res) => {
        if (state.counter) {
          window.clearInterval(state.counter);
          commit('setCounter', null);
          sessionStorage.removeItem("emailOtpTime");
        }

        if (res.data.is_paid_account) {
          commit('setIsPaidAccount', res.data.is_paid_account);
        }

        if (res.data.is_email_verified) {
          commit('setIsEmailVerified', res.data.is_email_verified);
        }

        if (res.data.consumer_account_id) {
          const caid = res.data.consumer_account_id.split('.')[0];
          commit('setCaid', caid, { root: true });
          writeTmCaid(res.data.consumer_account_id); // GTM tm_caid: full value; never throws
        }

        if (res.data.is_mfa_enable === '0') {
          // console.log('[signInAuth] is_mfa_enable === 0, checking email verification');

          // 檢查是否需要 email verification
          // console.log('[signInAuth] needEmailVerification check:', {
          //   needEmailVerification: getters.needEmailVerification,
          //   disableEmailVerification: state.disableEmailVerification,
          //   skipEmailVerification: state.skipEmailVerification,
          //   isEmailVerified: state.isEmailVerified,
          //   isPaidAccount: state.isPaidAccount,
          // });

          if (getters.needEmailVerification) {
            // console.log('[signInAuth] needEmailVerification = true, checking mode and signInType:', {
            //   isRedesign: getters.isRedesign,
            //   signInType: state.signInType,
            //   SIGNINTYPE_email: SIGNINTYPE.email,
            // });

            // redesign 模式：任何登入方式都需要驗證
            // 舊版模式：只有 email 登入需要驗證
            if (getters.isRedesign || state.signInType === SIGNINTYPE.email) {
              // console.log('[signInAuth] Redirecting to signInVerifyEmail');

              // Disarm the reauth cleanup listener before routing
              if (window.__reauthBeforeUnload) {
                window.removeEventListener('beforeunload', window.__reauthBeforeUnload);
                window.__reauthBeforeUnload = null;
              }
              clearPollingSession();

              router.push({
                name: 'auth/signInVerifyEmail',
                query: getters.isRedesign ? { scenario: 'signin' } : {},
              });
              return;
            // } else {
            //   console.log('[signInAuth] needEmailVerification = true but not redirecting (not redesign and not email signIn)');
            }
          // } else {
          //   console.log('[signInAuth] needEmailVerification = false, skipping email verification');
          }

          dispatch(
            'triggerEvent',
            {
              eventName: `SignInSuccess`,
              addInfo: {
                Method: state.signInType,
              },
            },
            { root: true },
          );

          // Disarm the reauth cleanup listener before form submit
          // to prevent cancelreauth from racing with the successful sign-in
          if (window.__reauthBeforeUnload) {
            window.removeEventListener('beforeunload', window.__reauthBeforeUnload);
            window.__reauthBeforeUnload = null;
          }

          clearPollingSession();
          signInForm.submit();
        }

        if (res.data.is_mfa_enable === '1') {
          commit('setTwoFactor', {
            isTwoFactor: true,
            availableMethod: res.data.mfa_method,
            methodEnum: res.data.mfa_method_enum,
            userSms: `(+${res.data.sms_country_code}) ${res.data.sms_phone_number_mask}`,
            userEmail: res.data.email_mask,
          });

          dispatch('triggerEvent', { eventName: `PageView-${state.defaultMethod}` }, { root: true });

          if (state.defaultMethod === 'sms' || state.defaultMethod === 'email') {
            dispatch('sendMFACode', { method: state.defaultMethod });
          }
        }
      })
      .catch(async (e) => {
        console.log('invalid credential'); // for client integration
        commit('ableToSignIn', true);

        // Handle 403 email_not_verified error in redesign mode
        if (
          e?.response?.status === 403 &&
          e?.response?.data?.error === 'email_not_verified' &&
          getters.isRedesign
        ) {
          console.log('[signInAuth] Email not verified, redirecting to verify_email');
          const email = e.response.data.email || userdata.email;
          const scenario = e.response.data.scenario || 'signin';

          // Set email to state for VerifyEmailOTP to use
          if (email) {
            commit('setEmailAccount', email);
          }

          router.push({
            name: 'auth/signInVerifyEmail',
            query: { email, scenario },
          });
          return;
        }

        const errorMsg = userdata.password
          ? state.lang.incorrectEmailPassword
          : state.lang.incorrectCode;

        commit('setSignInErrMsg', errorMsg);

        // send tc event
        let hexAccount = '';
        const isReAuth = router.currentRoute.path.includes('re_auth');

        if (userdata.email) {
          hexAccount = isReAuth ? userdata.email : await hashData(userdata.email, 'SHA-256');
        } else {
          const smsAccount = `${userdata.sms_country_code}-${userdata.sms_phone_number}`;
          hexAccount = isReAuth ? smsAccount : await hashData(smsAccount, 'SHA-256');
        }

        dispatch(
          'triggerEvent',
          {
            eventName: `SignInFail`,
            addInfo: {
              http_code: e?.response?.status,
              current_account: hexAccount,
            },
          },
          { root: true },
        );
      });
  },
  async signUpAuth({ commit, state, dispatch, rootState }, { userdata, signUpForm }) {
    commit('setEmailError', '');
    commit('setPhoneError', '');
    commit('setSignUpErrMsg', '');

    let recaptchaToken = '';
    let siteKey = '';

    try {
      const recaptchaResult = await state.recaptcha.execute('signUp');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    let signUpMethod;
    if (userdata.provider) {
      signUpMethod = userdata.provider;
    } else if (userdata.passwordless_otp_code && userdata.email) {
      signUpMethod = 'Email-otp';
    } else if (userdata.passwordless_otp_code) {
      signUpMethod = 'Phone';
    } else {
      signUpMethod = 'Email';
    }

    return request
      .post('/signin/module.php/tmauth/createtmaccount', {
        recaptchaToken,
        siteKey,
        lang: rootState.userLang,
        ...userdata,
      })
      .then((res) => {
        if (res.data.consumer_account_id) {
          const caid = res.data.consumer_account_id.split('.')[0];
          commit('setCaid', caid, { root: true });
          writeTmCaid(res.data.consumer_account_id); // GTM tm_caid: full value; never throws
        }

        dispatch(
          'triggerEvent',
          {
            eventName: `SignUpSuccess`,
            addInfo: {
              Method: signUpMethod,
            },
          },
          { root: true },
        );

        clearPollingSession();
        signUpForm.submit();
      })
      .catch((err) => {
        commit('setIsLoading', false);
        dispatch(
          'triggerEvent',
          {
            eventName: `SignUpFail`,
            addInfo: {
              Method: signUpMethod,
              http_code: err.response?.status,
            },
          },
          { root: true },
        );

        if (err.response?.status === 409) {
          console.log('[signUpAuth] 409 Conflict detected');
          console.log('[signUpAuth] Response data:', err.response?.data);
          const linkAuthMethod = err.response.data?.available_link_auth_method;
          if (linkAuthMethod && linkAuthMethod.includes('passwordless-sms')) {
            commit('setShowAccountLinkingNotice', true);
          } else {
            commit('setIsAccountDuplicated', true);
          }
        } else {
          commit('setSignUpErrMsg', rootState.lang.error_msg.common);
        }
      });
  },
  async passwordlessAccountLinking(
    { commit, dispatch, state, rootState },
    { userdata, signInForm },
  ) {
    commit('ableToSignIn', false);

    let recaptchaToken = '';
    let siteKey = '';

    try {
      const recaptchaResult = await state.recaptcha.execute('passwordlessLinkAccount');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    request
      .post('/signin/module.php/tmauth/passwordlesslinkaccount', {
        ...userdata,
        recaptchaToken,
        siteKey,
      })
      .then((res) => {
        if (res.data.is_mfa_enable === '1') {
          commit('setTwoFactor', {
            isTwoFactor: true,
            availableMethod: res.data.mfa_method,
            methodEnum: res.data.mfa_method_enum,
            userSms: `(+${res.data.sms_country_code}) ${res.data.sms_phone_number_mask}`,
            userEmail: res.data.email_mask,
          });

          dispatch('triggerEvent', { eventName: `PageView-${state.defaultMethod}` }, { root: true });

          if (state.defaultMethod === 'sms' || state.defaultMethod === 'email') {
            dispatch('sendMFACode', { method: state.defaultMethod });
          }
        } else {
          if (window.__reauthBeforeUnload) {
            window.removeEventListener('beforeunload', window.__reauthBeforeUnload);
            window.__reauthBeforeUnload = null;
          }

          clearPollingSession();
          signInForm.submit();
        }
      })
      .catch(async (e) => {
        if (e.response?.data?.message === 'Invalid credential.') {
          console.log('invalid credential'); // for client integration
          commit('setSignInErrMsg', state.lang.incorrectEmailPassword);
        } else {
          commit('setSignInErrMsg', rootState.lang.error_msg.common);
        }

        commit('ableToSignIn', true);

        // send tc event
        const smsAccount = `${userdata.sms_country_code}-${userdata.sms_phone_number}`;
        const hexAccount = await hashData(smsAccount, 'SHA-256');

        dispatch(
          'triggerEvent',
          {
            eventName: `SignInFail`,
            addInfo: {
              http_code: e?.response?.status,
              current_account: hexAccount,
            },
          },
          { root: true },
        );
      });
  },
  async cancelReauth() {
    try {
      await request.post('/signin/module.php/tmauth/cancelreauth');
    } catch (e) {
      // Best effort — if the API fails, still proceed with frontend cleanup
    }
  },
  async socialAccountLinking({ commit, state }, { password, signInForm }) {
    commit('ableToSignIn', false);

    let recaptchaToken = '';
    let siteKey = '';

    try {
      const recaptchaResult = await state.recaptcha.execute('socialLinkAccount');
      recaptchaToken = recaptchaResult.token;
      siteKey = recaptchaResult.siteKey;
    } catch (error) {
      // ignore
    }

    request
      .post('/signin/module.php/tmauth/sociallinkaccount', {
        password,
        recaptchaToken,
        siteKey,
      })
      .then(() => {
        if (window.__reauthBeforeUnload) {
          window.removeEventListener('beforeunload', window.__reauthBeforeUnload);
          window.__reauthBeforeUnload = null;
        }

        clearPollingSession();
        signInForm.submit();
      })
      .catch(() => {
        console.log('invalid credential'); // for client integration
        commit('ableToSignIn', true);
        commit('setSignInErrMsg', state.lang.incorrectPassword);
      });
  },
};
